import { ExternalLink, Sparkles } from 'lucide-react';

import { config } from '@/lib/config';

/**
 * AI Observability (Langfuse) deep-link card for the platform admin surface.
 *
 * Renders a plain anchor that opens the env-appropriate Langfuse URL in a new
 * tab. Deliberately NOT an iframe — Langfuse sets `X-Frame-Options: DENY`
 * globally (Phase 42 RESEARCH §P5). Auth is not pre-provided client-side —
 * the link flows through Traefik BasicAuth (middleware from Plan 42-01) then
 * Langfuse's own email/password login (T-42-03 defense-in-2-layers).
 *
 * Secret leakage guard: this component NEVER renders a Langfuse public key,
 * secret key, or any SDK token. Only the operator-facing dashboard URL is
 * emitted (threat model T-42-01).
 *
 * Phase 42 OBS-05.
 */

/**
 * Normalize a Vite env value. Vite can serialize absent vars to the literal
 * string "undefined" in some build contexts, and empty strings are equally
 * useless here. Treat both as "unset" so the caller can fall back cleanly.
 */
function nonEmpty(v: string | undefined): string | undefined {
  if (!v || v === 'undefined') return undefined;
  return v;
}

function resolveLangfuseUrl(): string {
  // From runtime config (the #app-config island), injected per request by the
  // server — so the build-once bundle resolves the correct env's Langfuse URL.
  // Falls back sensibly when unset (config.baseDomain self-derives from the
  // current hostname; appEnv defaults to 'production').
  const baseDomain = nonEmpty(config.baseDomain) ?? 'marine-dms.local';
  const appEnv     = nonEmpty(config.appEnv)     ?? 'local';

  // Routing:
  //   local   → https://langfuse.marine-dms.local
  //   staging → https://staging.langfuse.dms44.com   (<env>.<svc>.<base>)
  //   prod    → https://langfuse.dms44.com
  if (appEnv === 'staging') {
    return `https://staging.langfuse.${baseDomain}`;
  }
  return `https://langfuse.${baseDomain}`;
}

export function AIObservabilityLink() {
  const url = resolveLangfuseUrl();

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="ai-observability-link"
      className="group flex items-center gap-3 rounded-lg border border-border bg-card p-4 transition-colors hover:bg-accent"
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Sparkles className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          AI Observability (Langfuse)
          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground transition-colors group-hover:text-foreground" />
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Inspect AI traces, prompts, completions, and eval scores per feature and tenant.
        </p>
        <p className="mt-1 truncate text-xs text-muted-foreground">
          Requires operator credentials — {url}
        </p>
      </div>
    </a>
  );
}
